import { useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext/AuthContext';
import { usePageContext } from '../../context/PageContext/PageContext';
import { roleNameMap } from '../../constants/roles';
import AppSidebar from '../AppSidebar/AppSidebar';
import './MobileNav.css';

// Screens this wide show the menu beside the page (AppSidebar "rail"), so the
// panel closes if the screen grows past it, for example a tablet turned
// sideways.
const WIDE_SCREEN = '(min-width: 1024px)';

/**
 * The phone menu: a panel that slides in from the left with the same items as
 * the sidebar. Opened by the header's menu button below 1024 px for signed-in
 * users. Closes on Escape, a tap outside the panel, the close button, choosing
 * an item, a page change (PageContext) and the screen growing to 1024 px.
 */
export default function MobileNav({ role }) {
    const { profile } = useAuth();
    const { mobileSidebarOpen, setMobileSidebarOpen } = usePageContext();
    const closeButtonRef = useRef(null);

    const close = () => setMobileSidebarOpen(false);

    useEffect(() => {
        if (!mobileSidebarOpen) return undefined;
        const query = window.matchMedia(WIDE_SCREEN);
        const onChange = (event) => {
            if (event.matches) setMobileSidebarOpen(false);
        };
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, [mobileSidebarOpen, setMobileSidebarOpen]);

    return (
        <Dialog
            open={Boolean(mobileSidebarOpen)}
            onClose={close}
            initialFocus={closeButtonRef}
            className="mobile-nav"
        >
            <div className="mobile-nav-backdrop" aria-hidden="true" />
            <Dialog.Panel className="mobile-nav-panel">
                <div className="mobile-nav-header">
                    <Dialog.Title className="mobile-nav-title">
                        {roleNameMap[role]?.shortName ?? 'ParkPro'}
                    </Dialog.Title>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        className="mobile-nav-close"
                        onClick={close}
                        aria-label="Close menu"
                    >
                        <XMarkIcon aria-hidden="true" />
                    </button>
                </div>
                <div className="mobile-nav-body">
                    <AppSidebar roles={profile?.roles} variant="drawer" onNavigate={close} />
                </div>
            </Dialog.Panel>
        </Dialog>
    );
}
